
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Save } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useIsMobile } from "@/hooks/use-mobile";
import { SaveContactButton } from "./save-contact/SaveContactButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useContactBook } from "@/hooks/contact/useContactBook";
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

interface ContactDetails {
  isMyself: boolean;
  name: string;
  phone: string;
  email: string;
}

interface ContactSectionProps {
  title: string;
  contact: ContactDetails;
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneChange: (value: string) => void;
  onClear: () => void;
  inputPrefix: string;
  hideTitle?: boolean;
  contactIndex?: number;
  onContactFieldChange?: (field: string, value: string) => void;
  onContactUpdate?: (updates: Partial<ContactDetails>) => void; // New atomic update prop
}

const ContactSection = ({
  title,
  contact,
  onContactChange,
  onPhoneChange,
  onClear,
  inputPrefix,
  hideTitle = false,
  contactIndex,
  onContactFieldChange,
  onContactUpdate
}: ContactSectionProps) => {
  const isMobile = useIsMobile();
  const {
    savedContacts,
    isLoading
  } = useContactBook();

  // Find the "Myself" contact if it exists
  const myselfContact = savedContacts.find(c => c.label === 'Myself' || c.name === 'Myself');
  
  const handleSavedContactSelect = (contactId: string) => {
    const selectedContact = savedContacts.find(c => c.id === contactId);
    if (selectedContact) {
      console.log(`🎯 SELECTING SAVED CONTACT for ${title}:`, selectedContact);
      
      // Use atomic update if available (for MultiContactSection)
      if (onContactUpdate) {
        console.log(`🔄 Using atomic contact update for contact ${contactIndex}`);
        onContactUpdate({
          name: selectedContact.name,
          email: selectedContact.email || '',
          phone: selectedContact.phone
        });
      } else if (onContactFieldChange) {
        console.log(`🔄 Using direct field updates for contact ${contactIndex}`);
        onContactFieldChange('name', selectedContact.name);
        onContactFieldChange('email', selectedContact.email || '');
        onPhoneChange(selectedContact.phone);
      } else {
        // Fallback to synthetic events (for standalone usage)
        console.log(`🔄 Using synthetic events for standalone usage`);
        const nameEvent = {
          target: {
            name: 'name',
            value: selectedContact.name
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        const emailEvent = {
          target: {
            name: 'email',
            value: selectedContact.email || ''
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        onContactChange(nameEvent);
        onContactChange(emailEvent);
        onPhoneChange(selectedContact.phone);
      }
      
      toast({
        title: "Contact selected",
        description: `${selectedContact.name} has been selected.`
      });
    }
  };

  return (
    <div>
      {!hideTitle && <h2 className="text-xl font-semibold mb-2">{title}</h2>}
      <Card className="border-none shadow-none">
        <div className={`${isMobile ? 'flex-col space-y-3' : 'flex gap-4'}`}>
          <div className="space-y-2 flex-1">
            <Label htmlFor={`${inputPrefix}-name`}>Contact Name *</Label>
            <Input 
              id={`${inputPrefix}-name`} 
              name="name" 
              value={contact.name} 
              onChange={onContactChange} 
              className="w-full"
            />
          </div>

          {savedContacts.length > 0 && (
            <div className="space-y-2 flex-1">
              <Label>Choose Saved Contact</Label>
              <Select onValueChange={handleSavedContactSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Saved Contact" />
                </SelectTrigger>
                <SelectContent>
                  {savedContacts.map(savedContact => (
                    <SelectItem key={savedContact.id} value={savedContact.id}>
                      {savedContact.label || savedContact.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-2 mt-3">
          <Label htmlFor={`${inputPrefix}-phone`}>Contact Phone *</Label>
          <PhoneInput 
            id={`${inputPrefix}-phone`} 
            international 
            countryCallingCodeEditable={false} 
            defaultCountry="ZA" 
            value={contact.phone} 
            onChange={onPhoneChange} 
            className="w-full"
          />
        </div>

        <div className="space-y-2 mt-3">
          <Label htmlFor={`${inputPrefix}-email`}>Contact Email *</Label>
          <Input 
            id={`${inputPrefix}-email`} 
            type="email" 
            name="email" 
            value={contact.email || ''} 
            onChange={onContactChange} 
            className="w-full" 
            placeholder="example@email.com"
          />
        </div>

        <div className="flex justify-between mt-4">
          <Button variant="outline" size="sm" onClick={onClear}>
            <X className="h-4 w-4 mr-2" />
            {isMobile ? "Clear" : "Clear Contact"}
          </Button>
          <SaveContactButton name={contact.name} phone={contact.phone} email={contact.email} />
        </div>
      </Card>
    </div>
  );
};

export default ContactSection;
